import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { FormDrawer } from '@/components/feedback/drawer';
import { useToast } from '@/components/feedback/toast';
import { SelectField, TextAreaField, TextField } from '@/components/forms';
import type { OrderDetail, RaiseTicketValues } from '@/features/orders';
import {
  SUPPORT_TICKET_RAISED_MESSAGE,
  raiseTicketSchema,
  useOrderAssignees,
  useRaiseTicket,
} from '@/features/orders';
import { ApiError } from '@/lib/api/errors';

type Props = { order: OrderDetail; open: boolean; onClose: () => void };

export function RaiseTicketDrawer({ order, open, onClose }: Props) {
  const form = useForm<RaiseTicketValues>({
    resolver: zodResolver(raiseTicketSchema),
    defaultValues: { title: '', assigneeId: '', description: '' },
    mode: 'onTouched',
  });
  const assignees = useOrderAssignees(open);
  const raise = useRaiseTicket(order.id);
  const toast = useToast();

  const options = (assignees.data ?? []).map((a) => ({ value: a.id, label: a.name }));

  const submit = (values: RaiseTicketValues) =>
    raise.mutate(values, {
      onSuccess: () => {
        toast.success({ title: SUPPORT_TICKET_RAISED_MESSAGE });
        form.reset({ title: '', assigneeId: '', description: '' });
        onClose();
      },
      onError: (e) =>
        toast.error({
          title:
            e instanceof ApiError ? e.message : 'Could not raise the ticket. Please try again.',
        }),
    });

  return (
    <FormDrawer
      form={form}
      open={open}
      onClose={onClose}
      title="Raise a Ticket"
      submitText="Submit"
      onSubmit={submit}
      loading={raise.isPending}
    >
      <TextField name="title" control={form.control} label="Title" placeholder="Enter" required />
      <SelectField
        name="assigneeId"
        control={form.control}
        label="Choose Assignee"
        placeholder="Select"
        options={options}
        loading={assignees.isPending}
        required
      />
      <TextAreaField
        name="description"
        control={form.control}
        label="Description"
        placeholder="Enter"
        rows={5}
        required
      />
    </FormDrawer>
  );
}
